import React, { useEffect } from 'react';
import { DatePicker, Form, InputNumber, Modal, Select } from 'antd';
import type { FormInstance } from 'antd';
import type { Dayjs } from 'dayjs';
import ScheduleNextFollowupFields from '../../tasks/components/ScheduleNextFollowupFields';
import type { TaskPriority } from '../../tasks/types';
import type { ClosingStatus } from '../types';
import { defaultFollowUpTitle } from '../utils/followUpTitle';
import { closeLeadModalTitle } from '../utils/closingStatusHelpers';

export interface CloseLeadFormValues {
  closingStatus?: ClosingStatus;
  closedAmount?: number;
  actualClosingDate?: Dayjs;
  closingNote?: string;
  scheduleNext?: boolean;
  nextDueDate?: Dayjs;
  nextTitle?: string;
  nextDescription?: string;
  nextPriority?: TaskPriority;
}

export interface CloseLeadModalProps {
  open: boolean;
  form: FormInstance<CloseLeadFormValues>;
  onOk: () => void;
  onCancel: () => void;
  confirmLoading?: boolean;
  /** When set (pipeline drag), outcome is fixed and the status select is hidden. */
  fixedClosingStatus?: ClosingStatus;
  /** Lead title for default next follow-up task name (list close). */
  leadTitle?: string;
}

const CloseLeadModal: React.FC<CloseLeadModalProps> = ({
  open,
  form,
  onOk,
  onCancel,
  confirmLoading = false,
  fixedClosingStatus,
  leadTitle,
}) => {
  const selectedClosingStatus = Form.useWatch('closingStatus', form);
  const formClosingStatus = form.getFieldValue('closingStatus') as ClosingStatus | undefined;
  const effectiveClosingStatus =
    fixedClosingStatus ?? selectedClosingStatus ?? formClosingStatus;

  useEffect(() => {
    if (!open || !leadTitle) {
      return;
    }

    const currentTitle = form.getFieldValue('nextTitle') as string | undefined;
    form.setFieldsValue({
      scheduleNext: form.getFieldValue('scheduleNext') ?? true,
      ...(currentTitle ? {} : { nextTitle: defaultFollowUpTitle(leadTitle) }),
    });
  }, [open, leadTitle, form]);

  return (
    <Modal
      title={closeLeadModalTitle(effectiveClosingStatus)}
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      width={520}
      destroyOnClose
    >
      <div className="cp-form-section">
        <Form form={form} layout="vertical">
          {fixedClosingStatus === undefined && (
            <Form.Item name="closingStatus" label="Closing Status" rules={[{ required: true }]}>
              <Select<ClosingStatus>
                options={[
                  { value: 2 as ClosingStatus, label: 'Won' },
                  { value: 3 as ClosingStatus, label: 'Lost' },
                  { value: 4 as ClosingStatus, label: 'Disqualified' },
                ]}
              />
            </Form.Item>
          )}
          <Form.Item
            name="closedAmount"
            label="Closed Amount"
            rules={[{ required: true, message: 'Closed amount is required' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0.01} step={0.01} />
          </Form.Item>
          <Form.Item
            name="actualClosingDate"
            label="Actual Closing Date"
            rules={[{ required: true, message: 'Actual closing date is required' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <ScheduleNextFollowupFields showPriority />
        </Form>
      </div>
    </Modal>
  );
};

export default CloseLeadModal;
